import {forwardRef} from 'react';
import {useTranslate} from 'src/locales';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import {alpha, styled} from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';
import {RouterLink} from 'src/routes/components';
import Iconify from '../../iconify';
import {NavItemProps, NavItemStateProps} from '../types';
import IconComponent from '@components/icon/IconComponent';
import {CircularProgress} from '@mui/material';
import Label from 'src/minimal-components/label';
import { environment } from '@shared/config/environment';

// ----------------------------------------------------------------------

const StyledNavItem = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== 'active'
})<NavItemStateProps>(({active, open, depth, theme}) => {
  const subItem = depth !== 1;

  const opened = open && !active;

  const deepSubItem = Number(depth) > 2;

  const noWrapStyles = {
    width: '100%',
    maxWidth: '100%',
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  } as const;

  const baseStyles = {
    item: {
      marginBottom: 4,
      borderRadius: 8,
      color: !environment?.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED?theme.palette.text.secondary:'black',
      padding: theme.spacing(0.5, 1, 0.5, 1.5)
    },
    icon: {
      width: 24,
      height: 24,
      flexShrink: 0,
      marginRight: theme.spacing(2),
      color:  !environment?.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED?active? theme.palette.primary.main : theme.palette.common.white :active ? "white" : 'black'
    },
    label: {
      ...noWrapStyles,
      ...theme.typography.body2,
      textTransform: 'capitalize',
      fontWeight:
        theme.typography[active ? 'fontWeightSemiBold' : 'fontWeightMedium'],
      color: !environment?.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED?active ? theme.palette.primary.main : theme.palette.common.white: active ? "white"  : 'black'
    },
    caption: {
      ...noWrapStyles,
      ...theme.typography.caption,
      color: theme.palette.text.disabled
    },
    info: {
      display: 'inline-flex',
      marginLeft: theme.spacing(0.75)
    },
    arrow: {
      flexShrink: 0,
      marginLeft: theme.spacing(0.75),
      color: !environment?.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED?active ? theme.palette.primary.main : theme.palette.common.white: active ? theme.palette.primary.main : 'black'
    }
  } as const;

  return {
    // Root item
    ...(!subItem && {
      ...baseStyles.item,
      minHeight: 44,
      '& .icon': {
        ...baseStyles.icon
      },
      '& .sub-icon': {
        display: 'none'
      },
      '& .label': {
        ...baseStyles.label
      },
      '& .caption': {
        ...baseStyles.caption
      },
      '& .info': {
        ...baseStyles.info
      },
      '& .arrow': {
        ...baseStyles.arrow
      },
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.16)
      },
      ...(active && {
        color:
          "white",
        boxShadow:  !environment?.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED?theme.palette.mode === 'light'
            ? theme.palette.primary.main
            : theme.palette.primary.light: '0 6px 20px #82d1f580',
        marginBottom:  environment?.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED && 2,
        background: environment?.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED && 'transparent linear-gradient(116deg, #015aa7 0%, #00aae7 100%) 0% 0% no-repeat padding-box'
         ,

        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.16)
        }
      }),
      ...(opened && {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.action.hover
      })
    }),

    // Sub item
    ...(subItem && {
      ...baseStyles.item,
      minHeight: 36,
      '& .icon': {
        ...baseStyles.icon
      },
      '& .sub-icon': {
        ...baseStyles.icon,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:before': {
          content: '""',
          width: 4,
          height: 4,
          borderRadius: '50%',
          backgroundColor: theme.palette.text.disabled,
          transition: theme.transitions.create(['transform'], {
            duration: theme.transitions.duration.shorter
          }),
          ...(active && {
            transform: 'scale(2)',
            backgroundColor: theme.palette.primary.main
          })
        }
      },
      '& .label': {
        ...baseStyles.label
      },
      '& .caption': {
        ...baseStyles.caption
      },
      '& .info': {
        ...baseStyles.info
      },
      '& .arrow': {
        ...baseStyles.arrow
      },
      ...(active && {
        color: theme.palette.text.primary
      })
    }),

    // Deep sub item
    ...(deepSubItem && {
      paddingLeft: `${theme.spacing(Number(depth))} !important`
    })
  };
});

// ----------------------------------------------------------------------

const NavItem = forwardRef<HTMLDivElement, NavItemProps>(
  (
    {
      name,
      url,
      mainUrl,
      count,
      show_count,
      menuListData,
      icon,
      info,
      disabled,
      caption,
      roles,
      //
      open,
      depth,
      active,
      hasChild,
      externalLink,
      currentRole = 'admin',
      iframe,
      setExternalUrl,
      ...other
    },
    ref
  ) => {
    const subItem = depth !== 1;
    const totalCount = menuListData?.[mainUrl ?? '']?.count;

    const {t} = useTranslate();

    const renderContent = (
      <StyledNavItem
        ref={ref}
        disableGutters
        open={open}
        depth={depth}
        active={active}
        disabled={disabled}
        {...other}>
        {!subItem && icon && (
          <Box component="span" className="icon">
            <IconComponent iconName={icon} />
          </Box>
        )}

        {subItem && icon ? (
          <Box component="span" className="icon">
            <IconComponent iconName={icon} />
          </Box>
        ) : (
          <Box component="span" className="sub-icon" />
        )}

        {name && (
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: '1 1 auto',
              minWidth: 0,
              justifyContent: 'space-between'
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}>
              <Box component="span" className="label">
                {t(name)}
              </Box>
            </Box>
            {show_count && count != null && (
              <Box component="span" className="info">
                {menuListData?.[mainUrl ?? '']?.loader ? (
                  <CircularProgress size={12} />
                ) : (
                  <Tooltip
                    title={totalCount > 99 ? totalCount : ''}
                    placement="top"
                    disableHoverListener={totalCount <= 99}>
                    <Label
                      sx={{
                        color: 'white'
                      }}>
                      {count}
                    </Label>
                  </Tooltip>
                )}
              </Box>
            )}

            {caption && (
              <Tooltip title={caption} placement="top-start">
                <Box component="span" className="caption">
                  {caption}
                </Box>
              </Tooltip>
            )}
          </Box>
        )}

        {info && (
          <Box component="span" className="info">
            {info}
          </Box>
        )}

        {hasChild && (
          <Iconify
            width={16}
            className="arrow"
            icon={
              open
                ? 'eva:arrow-ios-downward-fill'
                : 'eva:arrow-ios-forward-fill'
            }
          />
        )}
      </StyledNavItem>
    );

    // Hidden item by role
    if (roles && !roles.includes(`${currentRole}`)) {
      return null;
    }

    if (hasChild) {
      return renderContent;
    }

    if (externalLink)
      return (
        <Link
          href={url}
          target="_blank"
          rel="noopener"
          color="inherit"
          underline="none"
          sx={{
            ...(disabled && {
              cursor: 'default'
            })
          }}>
          {renderContent}
        </Link>
      );

    return iframe ? (
      <Link
        component={RouterLink}
        href={url}
        onClick={() => {
          setExternalUrl(iframe);
        }}
        color="inherit"
        underline="none"
        sx={{
          ...(disabled && {
            cursor: 'default'
          })
        }}>
        {renderContent}
      </Link>
    ) : (
      <Link
        component={RouterLink}
        href={url}
        onClick={event => {
          if (disabled) {
            event.preventDefault(); // Prevent click and navigation
          }
        }}
        color="inherit"
        underline="none"
        sx={{
          ...(disabled && {
            cursor: 'default'
          })
        }}>
        {renderContent}
      </Link>
    );
  }
);

export default NavItem;

